/**
 * 24-Hour Line API: typed calls for every gated route of
 * docs/plans/oncall-line-plan.md 7.5, with the shapes of 7.6 and 2.3 (WP-03).
 *
 * Every call goes through the shared axios instance (services/api), which
 * attaches the Supabase JWT, X-Site-Id (the active site) and X-User-Id and
 * handles the 401 refresh and maintenance mode. Nothing under src/oncall
 * imports axios or builds a second client.
 *
 * Every route sits behind the backend guard createOncallGuard (WP-02) and
 * works on the line of the site in X-Site-Id, so switching site switches line
 * (plan 7.7). "admin" below means requireOncallAdmin: a super_admin, a
 * platform administrator, or tool_user_roles (oncall, admin) at the site.
 *
 *   GET    /health                       entitled   switches, Twilio, this site's line, last heartbeat,
 *                                                   active members, fallback note (admins), is_admin
 *   GET    /lines/health                 super_admin every site's line: last heartbeat, active members
 *   POST   /line                         admin      create this site's line (number and name)
 *   GET    /line                         entitled   the line and its 2.3 settings (null: no line yet)
 *   PATCH  /line                         admin      settings, name, fallback note
 *   GET    /members                      admin      the list, in hunt order
 *   POST   /members                      admin      add a member
 *   PATCH  /members/:id                  admin      edit, activate, deactivate, soft delete
 *   PUT    /members/order                admin      the new hunt order
 *   PUT    /members/:id/pin              admin      set the 6-digit dial-in PIN
 *   POST   /members/:id/test-call        admin      ring that cell with the press-1 test
 *   GET    /calls                        entitled   the call log, paged and filtered
 *   GET    /calls/:id                    entitled   attempts and the decrypted transcript (audited)
 *   GET    /calls/:id/voicemail          entitled   the voicemail audio, streamed (audited)
 *   PATCH  /calls/:id/review             entitled   the review state and note
 *   POST   /calls/:id/callback           entitled, linked member   the 6.2 callback bridge
 *
 * Column names are 7.6's, snake_case, as the rows come back; lists follow the
 * platform's {items, page} shape (the LMS precedent) and a single resource is
 * the row itself. PHI rules (plan 5): a caller's number and a transcript are
 * sent only to the entitled on-call team of the site; a staff member's full
 * number only to a line admin (everyone else gets the last four digits, see
 * lib/format.ts maskPhone). The client never logs a response body.
 */
import api from '../../services/api';

const BASE = '/api/oncall';

// ---------------------------------------------------------------------------
// Shared shapes and error readers
// ---------------------------------------------------------------------------

/** A list page: {items, page: {limit, offset, hasMore}} (the LMS Appendix A shape). */
export interface OncallPage {
  limit: number;
  offset: number;
  hasMore: boolean;
  /** The filtered total, when the route counts it. */
  total?: number | null;
}

export interface OncallList<T> {
  items: T[];
  page: OncallPage;
}

/** Every oncall route answers an error as { error, code } (codes like ONCALL_ADMIN_REQUIRED, ONCALL_NOT_FOUND). */
export interface OncallErrorBody {
  error?: string;
  code?: string;
  [extra: string]: unknown;
}

interface AxiosLikeError {
  response?: { status?: number; data?: OncallErrorBody };
  message?: string;
}

/** The server's error message, else `fallback` (never an empty string). */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const e = err as AxiosLikeError | null | undefined;
  const msg = e?.response?.data?.error;
  return typeof msg === 'string' && msg.trim() ? msg : fallback;
}

/** The server's error code (for example 'ONCALL_ADMIN_REQUIRED'), or null. */
export function apiErrorCode(err: unknown): string | null {
  const e = err as AxiosLikeError | null | undefined;
  const code = e?.response?.data?.code;
  return typeof code === 'string' && code ? code : null;
}

/** The HTTP status of a failed call, or null when the request never got an answer. */
export function apiErrorStatus(err: unknown): number | null {
  const e = err as AxiosLikeError | null | undefined;
  const status = e?.response?.status;
  return typeof status === 'number' ? status : null;
}

// ---------------------------------------------------------------------------
// Enumerations (7.6)
// ---------------------------------------------------------------------------

export type OncallCallDirection = 'inbound' | 'callback' | 'test' | 'heartbeat';

/** Monotonic: a status never moves backwards (7.8 rule 4). */
export type OncallCallStatus = 'ringing' | 'answered' | 'voicemail' | 'missed' | 'abandoned' | 'failed' | 'completed';

export type OncallAttemptOutcome = 'accepted' | 'no-answer' | 'busy' | 'failed' | 'screened-out' | 'canceled';

/** The light review state of plan 5: new -> reviewed. */
export type OncallReviewStatus = 'new' | 'reviewed';

/** The inbound track is the caller, the outbound track is staff (plan 5). */
export type OncallTrack = 'caller' | 'staff';

export type OncallRotation = 'rotate' | 'fixed';

// ---------------------------------------------------------------------------
// The line and its settings (2.3, 7.6 oncall_lines)
// ---------------------------------------------------------------------------

/**
 * A line's hunt settings (plan 2.3), clamped by the server on every read
 * (settings.js clampSettings). Ranges are the server's to enforce; the
 * Settings tab shows them.
 */
export interface OncallSettings {
  /** How long each attempt rings, 10 to 40 s (default 20). */
  ring_seconds: number;
  /** Phones rung at once, 1 to 10 (default 2). */
  wave_size: number;
  /** 'rotate' starts each call one member further down; 'fixed' always at the top. */
  rotation: OncallRotation;
  /** Full passes through the list before voicemail, 1 to 2 (default 1). */
  max_passes: number;
  /** Waves between "please continue to hold" messages, 0 to 10 (default 3; 0 never). */
  hold_message_every: number;
  voicemail_enabled: boolean;
  /** Who gets the no-PHI emails, 0 to 10 addresses (plan 3 D9). */
  alert_emails: string[];
  /** How far back the dial-in callback menu reaches, 1 to 90 days (default 30). */
  callback_window_days: number;
  transcription_enabled: boolean;
  /** Full-call recording; off by the owner's answer (Q4). */
  record_calls: boolean;
  /** The spoken texts (text-to-speech, English only). */
  greeting_text: string;
  hold_text: string;
  voicemail_text: string;
}

/** An oncall_lines row (7.6). One active line per site. */
export interface OncallLine {
  id: string;
  network_id: string;
  site_id: string;
  name: string;
  /** The line's own number, printed on the ICF (public, not masked). */
  twilio_number_e164: string;
  is_active: boolean;
  settings: OncallSettings;
  /** The phones the Twilio fallback Bin rings, kept in sync by hand (plan 4 layer 1). */
  fallback_note: string | null;
  rr_cursor?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: string | null;
  updated_by?: string | null;
}

/** POST /line: the runbook creates the site's line from the Settings tab (plan 11 step 5). */
export interface CreateLineInput {
  /** The number bought for the site; the server normalizes it to E.164. */
  twilio_number_e164: string;
  name: string;
}

/** PATCH /line: only the fields that change; settings are merged then clamped by the server. */
export interface UpdateLineInput {
  name?: string;
  fallback_note?: string | null;
  settings?: Partial<OncallSettings>;
}

// ---------------------------------------------------------------------------
// Health (7.5 GET /health, GET /lines/health)
// ---------------------------------------------------------------------------

/**
 * The 7.2 switches as the api reads them (routes/oncall/health.js
 * switchesSnapshot, WP-02): booleans and one count, never a number, a key, a
 * URL or a SID. A switch reads true only when its variable is the literal
 * 'true'; a configuration value reads true when it is set.
 */
export interface OncallSwitches {
  /** ONCALL_ENABLED: the hunt runs; otherwise every voice route answers 503 and the fallback Bin answers. */
  enabled: boolean;
  /** ONCALL_ALERT_SMS_ENABLED. */
  alert_sms_enabled: boolean;
  /** ONCALL_ALERT_MESSAGING_SERVICE_SID is set (no SMS without it). */
  alert_messaging_service_configured: boolean;
  /** ONCALL_HEARTBEAT_FROM is set. */
  heartbeat_configured: boolean;
  /** GCS_ONCALL_BUCKET is set (voicemail is refused closed without it). */
  voicemail_storage_configured: boolean;
  /** ONCALL_ENCRYPTION_KEY is present (transcript and caller-number writes fail closed without it). */
  encryption_key_configured: boolean;
  /** How many ONCALL_FALLBACK_NUMBERS are configured (plan 4 layer 3). */
  fallback_numbers_count: number;
  /** ONCALL_PUBLIC_API_URL is set (test calls, callbacks and the heartbeat need it). */
  public_api_url_configured: boolean;
  /** ONCALL_DIAL_OVERRIDE_TO is set: every outbound leg rings one test number (plan 8). */
  dial_override_active: boolean;
}

/** The line as the health routes summarize it. */
export type OncallLineSummary = Pick<OncallLine, 'id' | 'site_id' | 'name' | 'twilio_number_e164' | 'is_active'>;

/** GET /health (entitled): the Settings tab's health block, the shell's admin flag, the runbook's smoke. */
export interface OncallHealth {
  ok: boolean;
  feature: 'oncall';
  site_id: string;
  network_id: string;
  /** The caller is a line admin at this site (plan 7.5); the shell shows Team and Settings on it. */
  is_admin: boolean;
  is_super_admin: boolean;
  switches: OncallSwitches;
  /** TWILIO_ACCOUNT_SID and TWILIO_AUTH_TOKEN are present. */
  twilio_configured: boolean;
  /** This site's active line, or null before POST /line. */
  line: OncallLineSummary | null;
  /** The last heartbeat call that reached this line (plan 4 layer 4), or null. */
  last_heartbeat_at: string | null;
  /** Active, not deleted members of this site's line (0 without a line). */
  active_members: number;
  /** The fallback Bin's listed phones: line admins only, null for everyone else (plan 5). */
  fallback_note: string | null;
}

/** One line of GET /lines/health (super_admin): the All lines strip (plan 7.7). */
export interface OncallLineHealth extends OncallLineSummary {
  site_name: string | null;
  last_heartbeat_at: string | null;
  active_members: number;
}

/** GET /lines/health (super_admin): every site's active line, by site name. */
export interface OncallLinesHealth {
  lines: OncallLineHealth[];
  heartbeat_configured: boolean;
}

// ---------------------------------------------------------------------------
// Members (7.6 oncall_members; admin routes)
// ---------------------------------------------------------------------------

/**
 * An oncall_members row as the admin routes send it. The PIN hash never
 * leaves the server: pin_set says whether one exists.
 */
export interface OncallMember {
  id: string;
  line_id: string;
  display_name: string;
  /** The full number (the members routes are admin-only, plan 5). */
  phone_e164: string;
  /** Optional link to a platform user (the SiteOps callback button needs it, plan 6.2). */
  user_id: string | null;
  /** The linked user's name, when the route joins it. */
  user_name?: string | null;
  sort_order: number;
  is_active: boolean;
  pin_set: boolean;
  pin_failures?: number;
  /** Set while the PIN is locked after 5 failures (15 minutes, plan 6.1). */
  pin_locked_until: string | null;
  last_test_at: string | null;
  last_test_ok: boolean | null;
  deleted_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** GET /members: the list in hunt order, and the under-three warning (plan 4). */
export interface OncallMemberList {
  items: OncallMember[];
  active_count?: number;
  /** Server warnings, for example fewer than three active members. */
  warnings?: string[];
}

export interface CreateMemberInput {
  display_name: string;
  /** What the admin typed; the server normalizes it to E.164 (utils/phone.js toE164) and keeps it unique per line. */
  phone_e164: string;
  user_id?: string | null;
}

export interface UpdateMemberInput {
  display_name?: string;
  phone_e164?: string;
  user_id?: string | null;
  is_active?: boolean;
  /** Soft delete (the row keeps deleted_at; nothing is ever removed). */
  deleted?: boolean;
  /** Required to deactivate or delete the last active member (plan 4). */
  confirm?: boolean;
}

/** POST /members/:id/test-call: the call is placed; the press-1 result lands on the member row. */
export interface OncallTestCallResult {
  ok: boolean;
  call_id: string | null;
}

// ---------------------------------------------------------------------------
// Calls (7.6 oncall_calls, oncall_call_attempts, oncall_transcript_segments)
// ---------------------------------------------------------------------------

/** One row of the call log. */
export interface OncallCallSummary {
  id: string;
  line_id: string;
  direction: OncallCallDirection;
  status: OncallCallStatus;
  parent_call_id: string | null;
  started_at: string;
  answered_at: string | null;
  ended_at: string | null;
  duration_sec: number | null;
  answered_by_member_id: string | null;
  /** The member who took the call, for display. */
  answered_by_name?: string | null;
  /** The caller's last four digits (the full number stays encrypted at rest, plan 3 D8). */
  caller_last4?: string | null;
  /** A voicemail is stored in the bucket (the object key never leaves the server). */
  has_voicemail?: boolean;
  voicemail_duration_sec: number | null;
  review_status: OncallReviewStatus;
  reviewed_by: string | null;
  reviewed_by_name?: string | null;
  reviewed_at: string | null;
}

/** One ring of one member's phone (oncall_call_attempts). */
export interface OncallCallAttempt {
  attempt_no: number;
  wave_no: number;
  member_id: string | null;
  member_name?: string | null;
  rang_at: string | null;
  ended_at: string | null;
  outcome: OncallAttemptOutcome | null;
}

/** One decrypted transcript segment, after the cross-session merge (7.8 rule 3). */
export interface OncallTranscriptSegment {
  track: OncallTrack;
  text: string;
  created_at: string;
  sequence?: number;
}

/** GET /calls/:id: the detail drawer (plan 7.7). Reading it writes an audit row. */
export interface OncallCallDetail extends OncallCallSummary {
  review_note: string | null;
  attempts: OncallCallAttempt[];
  transcript: OncallTranscriptSegment[];
  /** Callbacks placed for this call (direction 'callback', parent_call_id = this id). */
  callbacks?: OncallCallSummary[];
  /** The viewer is an active member of this line linked to their user, so "Call back" may show (plan 6.2). */
  can_callback?: boolean;
}

/** GET /calls filters (plan 7.5; WP-10). Dates are ISO instants. */
export interface OncallCallFilters {
  status?: OncallCallStatus | OncallCallStatus[];
  review_status?: OncallReviewStatus;
  direction?: OncallCallDirection | OncallCallDirection[];
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface ReviewCallInput {
  review_status: OncallReviewStatus;
  review_note?: string | null;
}

/** POST /calls/:id/callback: `number` only for a new US number (plan 6.2); omitted, the original caller. */
export interface CallbackInput {
  number?: string;
}

export interface OncallCallbackResult {
  ok: boolean;
  /** The new callback call row. */
  call_id: string | null;
}

// ---------------------------------------------------------------------------
// Calls: health and identity
// ---------------------------------------------------------------------------

/** GET /api/oncall/health */
export async function getOncallHealth(): Promise<OncallHealth> {
  const response = await api.get<OncallHealth>(`${BASE}/health`);
  return response.data;
}

/** GET /api/oncall/lines/health (super_admin; 403 ONCALL_SUPER_ADMIN_REQUIRED otherwise). */
export async function getLinesHealth(): Promise<OncallLinesHealth> {
  const response = await api.get<OncallLinesHealth>(`${BASE}/lines/health`);
  return { ...response.data, lines: response.data?.lines ?? [] };
}

// ---------------------------------------------------------------------------
// Calls: the line
// ---------------------------------------------------------------------------

/**
 * GET /api/oncall/line: the site's line, or null when it has none yet (a
 * null body, or 404 ONCALL_NO_LINE). Any other failure throws.
 */
export async function getLine(): Promise<OncallLine | null> {
  try {
    const response = await api.get<unknown>(`${BASE}/line`);
    const data = response.data as { id?: unknown; line?: OncallLine | null } | null | undefined;
    if (!data || typeof data !== 'object') return null;
    // The row itself, or (tolerated) the row wrapped as { line }.
    if (typeof data.id === 'string') return data as unknown as OncallLine;
    return data.line ?? null;
  } catch (err) {
    if (apiErrorStatus(err) === 404 && apiErrorCode(err) === 'ONCALL_NO_LINE') return null;
    throw err;
  }
}

/** POST /api/oncall/line (admin): 409 when the site already has an active line. */
export async function createLine(input: CreateLineInput): Promise<OncallLine> {
  const response = await api.post<OncallLine>(`${BASE}/line`, input);
  return response.data;
}

/** PATCH /api/oncall/line (admin): the line after the change, settings clamped. */
export async function updateLine(patch: UpdateLineInput): Promise<OncallLine> {
  const response = await api.patch<OncallLine>(`${BASE}/line`, patch);
  return response.data;
}

// ---------------------------------------------------------------------------
// Calls: members (admin)
// ---------------------------------------------------------------------------

/** GET /api/oncall/members (admin), in hunt order. */
export async function listMembers(): Promise<OncallMemberList> {
  const response = await api.get<OncallMemberList | OncallMember[]>(`${BASE}/members`);
  const data = response.data;
  return Array.isArray(data) ? { items: data } : { ...data, items: data?.items ?? [] };
}

/** POST /api/oncall/members (admin). */
export async function createMember(input: CreateMemberInput): Promise<OncallMember> {
  const response = await api.post<OncallMember>(`${BASE}/members`, input);
  return response.data;
}

/** PATCH /api/oncall/members/:id (admin); the last active member needs confirm: true. */
export async function updateMember(memberId: string, patch: UpdateMemberInput): Promise<OncallMember> {
  const response = await api.patch<OncallMember>(`${BASE}/members/${encodeURIComponent(memberId)}`, patch);
  return response.data;
}

/** PUT /api/oncall/members/order (admin): every member id of the line, in the new hunt order. */
export async function reorderMembers(memberIds: string[]): Promise<OncallMemberList> {
  const response = await api.put<OncallMemberList | OncallMember[]>(`${BASE}/members/order`, { ids: memberIds });
  const data = response.data;
  return Array.isArray(data) ? { items: data } : { ...data, items: data?.items ?? [] };
}

/** PUT /api/oncall/members/:id/pin (admin): exactly 6 digits; the server stores only an scrypt hash. */
export async function setMemberPin(memberId: string, pin: string): Promise<OncallMember> {
  const response = await api.put<OncallMember>(`${BASE}/members/${encodeURIComponent(memberId)}/pin`, { pin });
  return response.data;
}

/** POST /api/oncall/members/:id/test-call (admin): rings that cell with the press-1 test. */
export async function sendTestCall(memberId: string): Promise<OncallTestCallResult> {
  const response = await api.post<OncallTestCallResult>(
    `${BASE}/members/${encodeURIComponent(memberId)}/test-call`,
    {},
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Calls: the call log
// ---------------------------------------------------------------------------

function callQuery(filters: OncallCallFilters): string {
  const params = new URLSearchParams();
  const add = (key: string, value: string | number | undefined) => {
    if (value !== undefined && value !== '') params.append(key, String(value));
  };
  const list = (value: string | string[] | undefined) => (Array.isArray(value) ? value.join(',') : value);
  add('status', list(filters.status));
  add('review_status', filters.review_status);
  add('direction', list(filters.direction));
  add('from', filters.from);
  add('to', filters.to);
  add('limit', filters.limit);
  add('offset', filters.offset);
  const query = params.toString();
  return query ? `?${query}` : '';
}

/** GET /api/oncall/calls: this site's line only, unreviewed calls first. */
export async function listCalls(filters: OncallCallFilters = {}): Promise<OncallList<OncallCallSummary>> {
  const response = await api.get<OncallList<OncallCallSummary>>(`${BASE}/calls${callQuery(filters)}`);
  return response.data;
}

/** GET /api/oncall/calls/:id (audited). A call of another site answers 404. */
export async function getCall(callId: string): Promise<OncallCallDetail> {
  const response = await api.get<OncallCallDetail>(`${BASE}/calls/${encodeURIComponent(callId)}`);
  return response.data;
}

/**
 * GET /api/oncall/calls/:id/voicemail (audited): the audio as a Blob. The
 * caller makes an object URL for the player and revokes it when done.
 */
export async function getVoicemailAudio(callId: string): Promise<Blob> {
  const response = await api.get<Blob>(`${BASE}/calls/${encodeURIComponent(callId)}/voicemail`, {
    responseType: 'blob',
  });
  return response.data;
}

/** PATCH /api/oncall/calls/:id/review: the call after the change. */
export async function reviewCall(callId: string, input: ReviewCallInput): Promise<OncallCallSummary> {
  const response = await api.patch<OncallCallSummary>(`${BASE}/calls/${encodeURIComponent(callId)}/review`, input);
  return response.data;
}

/**
 * POST /api/oncall/calls/:id/callback (entitled, linked member; plan 6.2):
 * SiteOps rings the member's own cell from the line's number, then bridges
 * to the caller (or to `number`, a new US number).
 */
export async function startCallback(callId: string, input: CallbackInput = {}): Promise<OncallCallbackResult> {
  const response = await api.post<OncallCallbackResult>(`${BASE}/calls/${encodeURIComponent(callId)}/callback`, input);
  return response.data;
}
